"use client";

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { BlockiesAvatar } from "@/components/blockies-avatar";
import {
  X,
  LogOut,
  Twitter,
  Link2,
  Unlink,
  MessageSquare,
  Copy,
  Check,
} from "lucide-react";
import Image from "next/image";

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { address, logout } = useAuth();
  const { data: socialUser } = useGetSocialUser();
  const { linkTwitter, unlinkTwitter, linkDiscord, unlinkDiscord } = useSocialPayMutations();

  const [copiedAddress, setCopiedAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    await logout();
    setIsLoading(false);
    onClose();
  };

  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }} // ease-out-quad
            className="fixed top-1/2 left-1/2 w-full max-w-md bg-white border border-swapper-primary/20 rounded-2xl z-[101] shadow-swapper-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Image
              src="/assets/logo.png"
              alt="Swapper"
              width={40}
              height={40}
              className="w-10 h-10"
            />
            <h2 className="text-xl font-bold text-swapper-secondary">
              User Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-swapper-light rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-swapper-gray" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Profile Section */}
          <div className="flex items-center gap-4">
            <BlockiesAvatar
              seed={address || ""}
              size={16}
              scale={4}
              className="border-2 border-swapper-primary"
            />
            <div className="flex-1">
              <p className="text-swapper-secondary font-semibold">
                {formatAddress(address)}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-swapper-gray text-sm">
                  Wallet Address
                </p>
                <button
                  onClick={handleCopyAddress}
                  className="p-1 hover:bg-swapper-light rounded transition-colors"
                >
                  {copiedAddress ? (
                    <Check className="w-3 h-3 text-swapper-primary" />
                  ) : (
                    <Copy className="w-3 h-3 text-swapper-gray" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Social Accounts */}
          <div className="space-y-3">
            <h3 className="text-swapper-secondary text-sm font-bold">
              Social Accounts
            </h3>

            <div className="space-y-2">
              {/* Twitter */}
              <div className="p-4 bg-swapper-light rounded-xl border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Twitter className="w-5 h-5 text-swapper-gray" />
                    <div>
                      <p className="text-swapper-secondary text-sm font-medium">X (Twitter)</p>
                      {(socialUser?.twitterMetadata || socialUser?.twitter) && (
                        <p className="text-swapper-gray text-xs">
                          @{socialUser?.twitterMetadata?.username || socialUser?.twitter?.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {(socialUser?.twitterMetadata || socialUser?.twitter) ? (
                    <button
                      onClick={() => unlinkTwitter.mutate()}
                      disabled={unlinkTwitter.isPending}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-swapper-secondary rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Unlink className="w-3 h-3" />
                      Unlink
                    </button>
                  ) : (
                    <button
                      onClick={() => linkTwitter.mutate({ redirectUrl: window.location.href })}
                      disabled={linkTwitter.isPending}
                      className="px-3 py-1.5 bg-swapper-primary hover:bg-swapper-primary/90 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Link2 className="w-3 h-3" />
                      Link
                    </button>
                  )}
                </div>
              </div>

              {/* Discord */}
              <div className="p-4 bg-swapper-light rounded-xl border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-swapper-gray" />
                    <div>
                      <p className="text-swapper-secondary text-sm font-medium">Discord</p>
                      {(socialUser?.discordMetadata || socialUser?.discord) && (
                        <p className="text-swapper-gray text-xs">
                          @{socialUser?.discordMetadata?.username || socialUser?.discord?.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {(socialUser?.discordMetadata || socialUser?.discord) ? (
                    <button
                      onClick={() => unlinkDiscord.mutate()}
                      disabled={unlinkDiscord.isPending}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-swapper-secondary rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Unlink className="w-3 h-3" />
                      Unlink
                    </button>
                  ) : (
                    <button
                      onClick={() => linkDiscord.mutate()}
                      disabled={linkDiscord.isPending}
                      className="px-3 py-1.5 bg-swapper-primary hover:bg-swapper-primary/90 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <Link2 className="w-3 h-3" />
                      Link
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={isLoading}
            className="w-full py-3 bg-swapper-secondary hover:bg-swapper-secondary/90 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <LogOut className="w-5 h-5" />
            {isLoading ? "Logging Out..." : "Logout"}
          </button>
        </div>
      </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};